// Smart backend configuration that switches between local and cloud
// NOTE: We derive staging vs prod primarily from the current hostname,
// so even if REACT_APP_ENV is wrong, the correct backend is used.
const APP_ENV = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_ENV) || 'prod';
const USE_LOCAL_BACKEND = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_USE_LOCAL_BACKEND === 'true');

// Helper: which Firebase Functions project to use for direct function calls
const getFunctionsProjectId = () => {
  // When using local backend in development, align Firebase project to staging
  // to keep auth/session/token project consistent across local testing.
  if (USE_LOCAL_BACKEND) {
    return 'artmosphere-89b64-staging';
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    const isStagingHost = host.includes('staging');
    if (isStagingHost) {
      return 'artmosphere-89b64-staging';
    }
  }
  if (APP_ENV === 'staging') {
    return 'artmosphere-89b64-staging';
  }
  // Default to production project
  return 'artmosphere-89b64';
};

const getBackendConfig = () => {
  // Ensure window is available
  if (typeof window === 'undefined') {
    // Fallback for SSR or initial load – assume non-local env
    const functionsProjectId = getFunctionsProjectId();
    const firebaseFunctionsUrl = `https://us-central1-${functionsProjectId}.cloudfunctions.net/api`;
    return {
      baseUrl: firebaseFunctionsUrl,
      type: 'firebase',
      environment: APP_ENV === 'staging' ? 'staging' : 'production'
    };
  }

  const host = window.location.hostname;
  const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';
  const isLanIp = /^192\.168\./.test(host) || /^10\./.test(host) || /^172\.(1[6-9]|2\d|3[0-1])\./.test(host);
  const envOverride = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_BASE_URL) ? process.env.REACT_APP_BACKEND_BASE_URL : null;
  const forceLocalBackend = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_USE_LOCAL_BACKEND === 'true');
  const isStagingHost = host.includes('staging');
  
  if (envOverride) {
    console.log('🔧 Using BACKEND override from env');
    return { baseUrl: envOverride, type: 'override', environment: isStagingHost || APP_ENV === 'staging' ? 'staging' : 'production' };
  }
  // Local backend whenever the UI runs on localhost/LAN — including `npm run build` + static
  // preview (`NODE_ENV=production`). Otherwise image proxies and verified-file routes hit cloud
  // URLs and tiles stay blank while `node index.js` serves API on :3001.
  if (isLocalhost || isLanIp || forceLocalBackend) {
    console.log('🔧 Using LOCAL backend (localhost/LAN)');
    return {
      baseUrl: 'http://localhost:3001',
      type: 'local',
      environment: 'development'
    };
  }
  
  // Use production backend (Cloud Run or Firebase Functions)
  // Default to Firebase Functions for production
  // You can override this with REACT_APP_BACKEND_BASE_URL environment variable
  const cloudRunUrl = process.env.REACT_APP_CLOUD_RUN_URL || 'https://artmosphere-backend-<region>-<project-id>.a.run.app';
  const functionsProjectId = getFunctionsProjectId();
  const firebaseFunctionsUrl = `https://us-central1-${functionsProjectId}.cloudfunctions.net/api`;
  
  // Check if we have a valid Cloud Run URL (not the placeholder)
  const useCloudRun = cloudRunUrl && !cloudRunUrl.includes('<region>') && !cloudRunUrl.includes('<project-id>');
  
  if (useCloudRun) {
    console.log('☁️ Using CLOUD RUN backend for', isStagingHost || APP_ENV === 'staging' ? 'staging' : 'production');
    return {
      baseUrl: cloudRunUrl,
      type: 'cloud-run',
      environment: isStagingHost || APP_ENV === 'staging' ? 'staging' : 'production'
    };
  }
  
  console.log('☁️ Using FIREBASE FUNCTIONS backend for', isStagingHost || APP_ENV === 'staging' ? 'staging' : 'production');
  return {
    baseUrl: firebaseFunctionsUrl,
    type: 'firebase',
    environment: isStagingHost || APP_ENV === 'staging' ? 'staging' : 'production'
  };
};

/**
 * Firebase Functions often use `baseUrl` `https://....cloudfunctions.net/api`.
 * Paths for Express routes must not add a second `/api` (`/api/api/...` 404s).
 * @param {string} baseUrl
 * @param {string} path - begins with `/api/...`, `/proxy-image`, etc.
 */
/**
 * Build a Functions-safe API URL: base ends with `/api` (function name); path is `/membership/...` not `/api/membership/...`.
 * Matches AuthContext `getApiBase()` + `/membership/status`.
 */
export function apiUrl(subpath) {
  const baseUrl = String(getBackendConfigInstance().baseUrl || '').replace(/\/$/, '');
  const apiBase = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
  let p = String(subpath || '');
  if (!p.startsWith('/')) p = `/${p}`;
  if (p.startsWith('/api/')) p = p.slice(4);
  return `${apiBase}${p}`;
}

export function joinBackendPath(baseUrl, path) {
  const base = String(baseUrl || '').replace(/\/$/, '');
  let p = String(path || '');
  if (!p.startsWith('/')) p = `/${p}`;
  // Firebase HTTPS function URLs look like `https://REGION-PROJECT.cloudfunctions.net/api` where the
  // trailing `/api` is the deployed function *name*, not Express's `/api` prefix. Stripping `/api`
  // from paths here produces `.../api/artwork-preview/...` so Express only sees `/artwork-preview/...`
  // and routes registered as `/api/artwork-preview` return 404 — thumbnails break in prod only.
  // Cloud Run bases may legitimately end with `/api`; keep stripping there to avoid `/api/api/...`.
  const isFirebaseHttpsApiFunction = /\.cloudfunctions\.net\/api$/i.test(base);
  if (!isFirebaseHttpsApiFunction && base.endsWith('/api') && p.startsWith('/api/')) {
    p = p.slice(4);
    if (!p.startsWith('/')) p = `/${p}`;
  }
  return `${base}${p}`;
}

/**
 * CRA dev server proxies `/api` and `/proxy-image` to the Node backend; `<img src="http://localhost:3001/...">`
 * bypasses that proxy and often fails. Rewrite those URLs to same-origin paths when the app runs on localhost/LAN.
 */
export function toSameOriginMediaUrl(url) {
  const s = String(url || '').trim();
  if (!s) return s;
  if (typeof window === 'undefined') return s;
  try {
    const here = window.location.hostname || '';
    const devHost =
      here === 'localhost' ||
      here === '127.0.0.1' ||
      /^192\.168\./.test(here) ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(here) ||
      /^10\./.test(here);
    if (!devHost) return s;
    const u = new URL(s);
    const path = `${u.pathname}${u.search}`;
    if (path.startsWith('/api/') || path.startsWith('/proxy-image')) {
      return path;
    }
    return s;
  } catch {
    return s;
  }
}

// Use a getter function to ensure window is available
let cachedConfig = null;
export const getBackendConfigInstance = () => {
  if (!cachedConfig) {
    cachedConfig = getBackendConfig();
  }
  return cachedConfig;
};

export const backendConfig = getBackendConfigInstance();

// Base URL for direct callable Firebase Functions by environment
const functionsProjectId = getFunctionsProjectId();
const firebaseFunctionsBase = `https://us-central1-${functionsProjectId}.cloudfunctions.net`;

// API endpoints with automatic switching
export const API_ENDPOINTS = {
  backendBase: backendConfig.baseUrl,
  generateStoryScene: apiUrl('/api/story/generate-scene'),
  testSceneGeneration: apiUrl('/api/story/test-scene-generation'),
  generateAppreciation: apiUrl('/api/artwork-appreciation'),
  // Firebase Functions endpoints for background workers
  generateAppreciationFirebase: `${firebaseFunctionsBase}/generateAppreciation`,
  askMentor: apiUrl('/api/ask-mentor'),
  uploadArtwork: apiUrl('/api/upload-artwork'),
  processTags: apiUrl('/api/process-tags'),
  refineArtwork: apiUrl('/api/refine-artwork'),
  reimagineArtwork: apiUrl('/api/reimagine/reimagine'),
  // Firebase Functions endpoints for background workers
  reimagineArtworkFirebase: `${firebaseFunctionsBase}/reimagineArtwork`,
  reimagineAppreciation: apiUrl('/api/reimagine/appreciation'),
  
  // Additional endpoints used throughout the app
  generateStoryImage: apiUrl('/api/story/generate-artwork'),
  // Firebase Functions endpoints for background workers
  generateStoryImageFirebase: `${firebaseFunctionsBase}/generateStoryImage`,
  // Job status endpoint
  jobStatus: apiUrl('/api/job-status'),
  editArtwork: apiUrl('/api/edit-artwork'),
  getKudosCount: apiUrl('/api/kudos/count'),
  addKudos: apiUrl('/api/kudos/add'),
  removeKudos: apiUrl('/api/kudos/remove'),
  getKudosList: apiUrl('/api/kudos/list'),
  getKudosForArtist: apiUrl('/api/kudos/for-artist'),
  getComments: apiUrl('/api/comments/get'),
  addComment: apiUrl('/api/comments/add'),
  deleteComment: apiUrl('/api/comments/delete'),
  getPredefinedComments: apiUrl('/api/comments/predefined'),
  getNotifications: apiUrl('/api/notifications'),
  markNotificationRead: apiUrl('/api/notifications/mark-read'),
  
  // Storyboarding endpoints - using Firebase Functions
  refineStoryboardingScene: `${firebaseFunctionsBase}/refineStoryboardingScene`,
  reimagineStoryboardingScene: `${firebaseFunctionsBase}/reimagineStoryboardingScene`,
  storyboardingAutoTag: `${firebaseFunctionsBase}/storyboardingAutoTag`,
  storyboardingSaveCharacterProfile: `${firebaseFunctionsBase}/storyboardingSaveCharacterProfile`,
  getPreviousScenes: `${firebaseFunctionsBase}/getPreviousScenes`,
  
  // New scene conceptualization endpoint
  conceptualizeScene: apiUrl('/api/story-generation/conceptualize-scene'),
  uploadSketch: apiUrl('/api/upload-sketch'),
  
  // Scene editing endpoint
  editSceneImage: apiUrl('/api/story-generation/edit-scene-image'),
  
  // Character endpoints
  createCharacter: apiUrl('/api/characters/create'),
  generateCharacterImage: apiUrl('/api/characters/generate-image'),
  editCharacterImage: apiUrl('/api/characters/edit-image'),
  saveToLibrary: apiUrl('/api/characters/save-to-library'),
  getUserLibrary: apiUrl('/api/characters/library'),
  
  // Tutorial endpoints
  generateTutorial: apiUrl('/api/tutorial/generate'),
  tutorialStatus: apiUrl('/api/tutorial/status'),
};

// Log current configuration
console.log('🎯 Backend Configuration:', {
  type: backendConfig.type,
  environment: backendConfig.environment,
  baseUrl: backendConfig.baseUrl,
  note: 'Using test server for kudos validation'
}); 